<script setup lang="ts">
import {
  ceilDate,
  floorDate,
  IconArrowCircleRight,
  isBeforeToday,
  isSameDate,
  isSameMonth,
  useMonthYearString,
} from '#shared'
import { computed, ref, watch } from 'vue'
import { useCalendarRows } from '../../lib/use-calendar-rows'

const { scheduledAt } = defineProps<{ scheduledAt: Date | null }>()

const emit = defineEmits<{ 'date-select': [date: Date]; 'close': [] }>()

const calendarPosition = ref<Date | null>(scheduledAt)
watch(
  () => scheduledAt,
  scheduledAt => (calendarPosition.value = scheduledAt),
  { immediate: true }
)

const prevCalendarPosition = computed(() => {
  const prevCalendarPosition = new Date(calendarPosition.value ?? new Date())
  prevCalendarPosition.setMonth(prevCalendarPosition.getMonth() - 1)
  return prevCalendarPosition
})

const nextCalendarPosition = computed(() => {
  const nextCalendarPosition = new Date(calendarPosition.value ?? new Date())
  nextCalendarPosition.setMonth(nextCalendarPosition.getMonth() + 1)
  return nextCalendarPosition
})

const canGoBack = computed(() => {
  let minCalendarPosition = new Date()
  minCalendarPosition = floorDate(minCalendarPosition, { unit: 'month' })

  return prevCalendarPosition.value >= minCalendarPosition
})

const goBack = () => (calendarPosition.value = prevCalendarPosition.value)

const canGoForward = computed(() => {
  let maxCalendarPosition = new Date()
  maxCalendarPosition.setFullYear(maxCalendarPosition.getFullYear() + 1)
  maxCalendarPosition = ceilDate(maxCalendarPosition, { unit: 'month' })

  return nextCalendarPosition.value <= maxCalendarPosition
})

const goForward = () => (calendarPosition.value = nextCalendarPosition.value)

const monthYearString = useMonthYearString(calendarPosition)
const calendarRows = useCalendarRows(calendarPosition)

const isDateSelectable = (date: Date) => {
  let maxSelectableDate = new Date()
  maxSelectableDate.setFullYear(maxSelectableDate.getFullYear() + 1)
  maxSelectableDate = ceilDate(maxSelectableDate, { unit: 'day' })
  if (isBeforeToday(date)) return false
  return date <= maxSelectableDate
}

const onDateSelect = (date: Date) => {
  if (!isDateSelectable(date)) return
  emit('date-select', date)
  emit('close')
}

const onCancel = () => {
  emit('close')
}
</script>

<template>
  <div :class="$style.root">
    <header :class="$style.header">
      <button :class="$style.navButton" @click="goBack" :disabled="!canGoBack">
        <IconArrowCircleRight :class="$style.iconReversed" />
      </button>
      <div :class="$style.monthLabel">{{ monthYearString }}</div>
      <button :class="$style.navButton" @click="goForward" :disabled="!canGoForward">
        <IconArrowCircleRight />
      </button>
    </header>

    <table :class="$style.main">
      <thead>
        <tr :class="$style.row">
          <th :class="[$style.cell, $style.cellHeader]">Sun</th>
          <th :class="[$style.cell, $style.cellHeader]">Mon</th>
          <th :class="[$style.cell, $style.cellHeader]">Tue</th>
          <th :class="[$style.cell, $style.cellHeader]">Wed</th>
          <th :class="[$style.cell, $style.cellHeader]">Thu</th>
          <th :class="[$style.cell, $style.cellHeader]">Fri</th>
          <th :class="[$style.cell, $style.cellHeader]">Sat</th>
        </tr>
      </thead>

      <tbody :class="$style.body">
        <tr v-for="(row, rowIndex) in calendarRows" :key="rowIndex" :class="$style.row">
          <td v-for="cell in row" :key="cell.value.getTime()" :class="$style.cell">
            <button
              :class="[
                $style.option,
                {
                  [$style.optionNotCurrentMonth]: !isSameMonth(cell.value, calendarPosition),
                  [$style.optionSelected]: isSameDate(cell.value, scheduledAt),
                },
              ]"
              :disabled="!isDateSelectable(cell.value)"
              @click="onDateSelect(cell.value)"
            >
              {{ cell.label }}
            </button>
          </td>
        </tr>
      </tbody>
    </table>

    <div :class="$style.actions">
      <button :class="$style.cancel" @click="onCancel">Cancel</button>
    </div>
  </div>
</template>

<style lang="scss" module>
@use '#mixins/button' as button;

.root {
  padding: 10px 15px 15px;
}

.header {
  padding-bottom: 5px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.monthLabel {
  font-size: 16px;
  line-height: 19px;
}

.iconReversed {
  transform: rotate(180deg);
}

.navButton {
  color: white;

  &:disabled {
    color: #aaaaaa;
  }
}

.main {
  width: 100%;
}

.body {
  display: flex;
  flex-direction: column;
  min-height: 240px;
}

.row {
  display: flex;
  justify-content: space-between;
}

.cell {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.cellHeader {
  font-size: 12px;
  line-height: 100%;
  font-weight: 700;
  color: #c0c0c0;
  text-transform: uppercase;
}

.option {
  color: white;
  border-radius: 7px;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition:
    border 0.1s ease-in-out,
    background-color 0.1s ease-in-out;

  &:disabled {
    color: #aaaaaa;
  }

  &:hover:not(:disabled) {
    border: 2px solid white;
  }
}

.optionNotCurrentMonth {
  color: #5f5f5f;
}

.optionSelected {
  color: white;
  background-color: #44446a;
}

.actions {
  margin-top: 15px;
}

.cancel {
  @include button.text;
}
</style>
